import React from "react"
import PropTypes from "prop-types"
import { useNavigate, useLocation } from "react-router-dom"
import { useDispatch } from "react-redux"
import { Avatar, Menu, MenuItem, IconButton, Tooltip, Button } from "@mui/material"
import {
    LogoutOutlined,
    MenuBookOutlined,
    ArrowBack
} from "@mui/icons-material"
import { getCurrentStudent, clearStudentSession } from "../session"
import { studentLearnAction } from "../../Redux/actions/lecture.action"
import { learnPaths, learnNavItems } from "../constants"

const initials = (student) => {
    const name = `${student?.firstname || ""} ${student?.lastname || ""}`.trim()
    if (!name) return "S"
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase()
}

/**
 * The lecture portal's own header. It replaces the app-wide navbar for every
 * route under /learn -- the common header hides itself there -- so entering
 * the portal visibly switches the user into the learning context.
 *
 * `minimal` is used on the sign-in screens, where there is no student yet:
 * it keeps the branding and offers a way back to the main site.
 */
export default function LearnHeader({ minimal = false }) {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const student = getCurrentStudent()
    const [anchorEl, setAnchorEl] = React.useState(null)

    const isActive = (path) => location.pathname.startsWith(path)

    const handleLogout = () => {
        setAnchorEl(null)
        clearStudentSession()
        /* Drop cached courses and video links so the next student starts clean. */
        dispatch(studentLearnAction.util.resetApiState())
        navigate(learnPaths.login, { replace: true })
    }

    return (
        <header
            className={
                minimal
                    ? "border-b border-white/15 bg-white/10 backdrop-blur"
                    : "sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur"
            }
        >
            <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">

                <button
                    type="button"
                    className="flex min-w-0 items-center gap-2 text-left sm:gap-3"
                    onClick={() =>
                        navigate(minimal ? learnPaths.login : learnPaths.home)
                    }
                >
                    <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white sm:h-10 sm:w-10 ${
                            minimal ? "bg-white/20" : "bg-indigo-600"
                        }`}
                    >
                        <MenuBookOutlined fontSize="small" />
                    </span>
                    <span className="min-w-0">
                        <span
                            className={`block truncate text-sm font-bold sm:text-base ${
                                minimal ? "text-white" : "text-slate-800"
                            }`}
                        >
                            Lecture Portal
                        </span>
                        {!minimal && student?.class_name && (
                            <span className="block truncate text-xs text-slate-500">
                                {student.class_name}
                            </span>
                        )}
                    </span>
                </button>

                {/* Portal navigation -- hidden on the sign-in screens */}
                {!minimal && (
                    <nav className="flex items-center gap-1 sm:gap-2">
                        {learnNavItems.map((item) => (
                            <button
                                type="button"
                                key={item.path}
                                onClick={() => navigate(item.path)}
                                className={`rounded-lg px-2.5 py-2 text-xs font-semibold transition sm:px-4 sm:text-sm ${
                                    isActive(item.path)
                                        ? "bg-indigo-50 text-indigo-700"
                                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                                }`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </nav>
                )}

                {minimal ? (
                    <Button
                        size="small"
                        startIcon={<ArrowBack />}
                        sx={{
                            color: "#fff",
                            textTransform: "none",
                            fontWeight: 600,
                            whiteSpace: "nowrap"
                        }}
                        onClick={() => navigate("/")}
                    >
                        <span className="hidden sm:inline">
                            Back to school site
                        </span>
                        <span className="sm:hidden">Back</span>
                    </Button>
                ) : (
                    <div className="flex items-center gap-2">
                        <div className="hidden text-right sm:block">
                            <p className="truncate text-sm font-semibold text-slate-800">
                                {student?.firstname} {student?.lastname}
                            </p>
                            {student?.roll_no && (
                                <p className="text-xs text-slate-500">
                                    Roll no. {student.roll_no}
                                </p>
                            )}
                        </div>

                        <Tooltip title="Account">
                            <IconButton
                                onClick={(event) =>
                                    setAnchorEl(event.currentTarget)
                                }
                                size="small"
                            >
                                <Avatar
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: "#4f46e5",
                                        fontSize: 14
                                    }}
                                >
                                    {initials(student)}
                                </Avatar>
                            </IconButton>
                        </Tooltip>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                        >
                            <MenuItem disabled>{student?.email}</MenuItem>
                            <MenuItem onClick={handleLogout}>
                                <LogoutOutlined
                                    fontSize="small"
                                    className="mr-2"
                                />
                                Sign out
                            </MenuItem>
                        </Menu>
                    </div>
                )}
            </div>
        </header>
    )
}

LearnHeader.propTypes = {
    minimal: PropTypes.bool
}
